import { ApolloError } from '@apollo/client';

import type { SingleExecutionResult } from '@apollo/client';

type UserFacingError = { message?: string | null } | null | undefined;

export const PERMISSION_ERROR_MESSAGE = 'You are not allowed to perform this action.';

/**
 * This function is useful for rare scenarios where we allow partial data and errors, i.e., the
 * errorPolicy is set to 'all', in order to handle EXPECTED permissions-related errors separately
 * from other instances of ApolloError.
 *
 * There may be rare instances where a given user has the permission to see a
 * set of records that are scoped, such as a care provider's profile, but cannot see certain
 * associated records due to those records' associations with other records that prevent access.
 *
 * For example, a provider with a "flex" care navigator customer assignment that is assigned to
 * a customer that does not allow offshore care support.
 */
export const extractPermissionErrorForPath = (
  error: ApolloError | undefined,
  gqlFieldPath: string[]
): {
  hasPermissionErrorForPath: boolean;
  nonPermissionErrors: ApolloError | undefined;
} => {
  // Adding some checks if used in non-TS files
  if (!error || !error.graphQLErrors.length || !gqlFieldPath || !gqlFieldPath.length) {
    return { hasPermissionErrorForPath: false, nonPermissionErrors: error };
  }

  // Given an error path like ['care_provider', 'care_navigator_customer_assignment'], is the
  // error a permission error?
  const permissionErrors = hasPermissionErrorForPath(error, gqlFieldPath);

  // Get non-permission errors
  const nonPermissionErrors = error.graphQLErrors.filter((err) => err.message !== PERMISSION_ERROR_MESSAGE);

  // Recontruct the ApolloError object without permission errors
  const nonPermissionApolloError = new ApolloError({
    ...error,
    graphQLErrors: nonPermissionErrors.length ? nonPermissionErrors : undefined,
  });

  return { hasPermissionErrorForPath: permissionErrors, nonPermissionErrors: nonPermissionApolloError };
};

const hasPermissionErrorForPath = (error: ApolloError | undefined, gqlFieldPath: string[]) => {
  if (!error || !error.graphQLErrors.length || !gqlFieldPath || !gqlFieldPath.length) {
    return false;
  }

  const gqlFieldPathString = gqlFieldPath.join('.');

  return error.graphQLErrors.some(
    (err) => err.message === PERMISSION_ERROR_MESSAGE && (err?.path || []).join('.') === gqlFieldPathString
  );
};

/**
 * Function to retrieve the collection of error messages that can be raised by user-facing errors
 * from the GQL server, i.e. not GraphQL execution errors or Network errors.
 */
export const getUserFacingErrorMessages = (
  errors?: SingleExecutionResult['errors'] | ReadonlyArray<UserFacingError> | null
) => {
  const messages = errors
    ?.map((error) => error?.message)
    .filter((message): message is string => typeof message === 'string' && message.trim().length > 0);

  return messages?.length ? messages.join('. ') : undefined;
};
